<template>
  <model-form
    v-if="currentUser && user"
    ref="modelForm"
    :model="user"
    modal-type="modal"
    :title="employeeCreation ? $t('user.employeeDetails') : $t('membership.memberDetails')"
    :show-footer-actions="showFooterActions"
    :submodel="submodel"
    :maximized="$q.screen.lt.sm"
    :width="!!width ? width : undefined"
    :is-dialog="isDialog"
    :read-only="readOnly"
    :borderless="borderless"
    :no-closed-hooks-setup="noClosedHooksSetup"
    data-test-id="member-form"
    @apply-changes="$emit('apply-changes')"
    @add-model="$emit('add-model')"
    @model-created="$emit('model-created', user)"
  >
    <template
      #header
    >
      <div v-if="hideHeader" />
    </template>
    <template
      #top-right-action-button
    >
      <div v-if="hideTopRightButton" />
    </template>
    <div
      class="column"
      data-test-id="member-form-details-container"
    >
      <div
        v-if="!simplify"
        class="row text-h6 q-col-gutter-md q-mt-sm"
      >
        <div>
          Provide {{ employeeCreation ? 'employee' : 'member' }} details
        </div>
      </div>
      <div
        class="row q-col-gutter-md q-mt-sm"
      >
        <q-input
          v-model.trim="user.firstName"
          class="col-6"
          :class="{ 'borderless': readOnly }"
          type="text"
          :label="$t('form.labels.firstName')"
          bottom-slots
          :required="user.type !== USER_TYPES.GUEST"
          :readonly="(user.accountClaimed && user.id !== currentUser.id && !$register) || readOnly"
          autofocus
          debounce="100"
          data-test-id="new-user-first-name-input"
          :rules="[val => (!!val || user.type === USER_TYPES.GUEST) || $t('form.errors.required')]"
        />
        <q-input
          v-model.trim="user.lastName"
          class="col-6"
          :class="{ 'borderless': readOnly }"
          type="text"
          :label="$t('form.labels.lastName')"
          bottom-slots
          :required="user.type !== USER_TYPES.GUEST"
          :readonly="(user.accountClaimed && user.id !== currentUser.id && !$register) || readOnly"
          debounce="100"
          data-test-id="new-user-last-name-input"
          :rules="[val => (!!val || user.type === USER_TYPES.GUEST) || $t('form.errors.required')]"
        />
      </div>
      <q-banner
        v-if="findUserByName && findUserByName.isRemote && findUserByName.id !== user.id && user.firstName && user.lastName"
        class="bg-warning q-mb-md row items-center"
      >
        <template #avatar>
          <q-icon
            name="warning"
            color="negative"
          />
        </template>
        <div class="row justify-between items-center">
          User with this name already exists
          <img
            :src="findUserByName.photoSmallUrl"
            style="width: 75px;"
          >
        </div>
      </q-banner>
      <div
        v-if="$register || (user.id === currentUser.id && currentCircuit.registrationOptions.prompt_for_address) && geoapifyEnabled"
        class="row q-col-gutter-md q-mt-xs"
      >
        <address-input
          :user="user"
        />
      </div>
      <div
        v-if="isInvoiceUser && $register"
        class="row q-col-gutter-md q-mt-xs"
      >
        <q-input
          v-model.trim="user.companyName"
          class="col-6"
          :class="{ 'borderless': readOnly }"
          type="text"
          label="Company Name"
          bottom-slots
          :readonly="(user.accountClaimed && user.id !== currentUser.id && !$register) || readOnly"
          autofocus
          debounce="100"
        />
        <q-select
          v-model="user.billingAddressDetails"
          label="Billing Address Details"
          class="col-6"
          :class="{ 'borderless': readOnly }"
          :readonly="(user.accountClaimed && user.id !== currentUser.id && !$register) || readOnly"
          multiple
          use-input
          use-chips
          new-value-mode="add"
        />
      </div>
      <div
        class="row q-col-gutter-md q-mt-xs"
      >
        <q-input
          id="email"
          v-model.trim="user.email"
          :class="{
            'borderless': readOnly,
            'col-6': isDialog || $q.screen.gt.xs,
            'col-12': $q.screen.xs && !isDialog,
          }"
          type="email"
          :label="$t('form.labels.emailAddress')"
          bottom-slots
          required
          :readonly="(user.accountClaimed && user.id !== currentUser.id && !$register) || readOnly"
          color="grey"
          debounce="100"
          data-test-id="new-user-email-input"
          :rules="[val => !user.isMembershipOwner || !!val || $t('form.errors.required'), emailAvailable]"
          lazy-rules
        />
        <phone-number-input
          :phone="user.phone"
          :override-country-code="user.phoneCountryCode"
          :class="{
            'borderless': readOnly,
            'col-6': isDialog || $q.screen.gt.xs,
            'col-12': $q.screen.xs && !isDialog,
            'q-mt-md': true
          }"
          :readonly="(user.accountClaimed && user.id !== currentUser.id && !$register && !!user.originalData.phone) || readOnly"
          :label="$t('form.labels.phoneNumber')"
          bottom-slots
          data-test-id="new-user-phone-input"
          @phone-changed="handlePhoneChange"
        />
      </div>
      <div
        class="row q-col-gutter-md q-mt-xs"
      >
        <iso-date-input
          v-if="!(isInvoiceUser || user.isInvoiceable)"
          v-model="user.dateOfBirth"
          emit-string="nullify-blank"
          :class="isDialog || $q.screen.gt.xs ? 'col-6' : 'col-12'"
          color="grey"
          :label="$t('form.labels.dateOfBirth')"
          bottom-slots
          :disable="(user.dateOfBirthVerified && !$register && !$admin) ||
            (user.accountClaimed && user.id !== currentUser.id && !$register && !$admin && !!user.originalData.dateOfBirth)|| readOnly"
          :read-only="readOnly"
        />
        <q-select
          v-if="shouldDisplayUserClass"
          ref="userClassSelect"
          v-model="user.userClass"
          :class="{
            'borderless': readOnly,
            'col-6': isDialog || $q.screen.gt.xs,
            'col-12': $q.screen.xs && !isDialog,
          }"
          :label="$t('user.userClass')"
          :options="userClassesForSelect"
          :display-value="user.userClass && user.userClass.name || 'None'"
          emit-value
          map-options
          bottom-slots
          data-test-id="new-user-class-input"
          color="grey"
          :bg-color="readOnly ? 'transparent' : undefined"
          :filled="readOnly"
          :readonly="readOnly"
          :rules="[val => !!val && val.isRemote || $t('form.errors.required')]"
        >
          <template #option="scope">
            <q-item
              v-bind="scope.itemProps"
              v-on="scope.itemEvents"
            >
              <q-item-section
                :class="{ 'text-bold': scope.selected }"
              >
                <q-item-label>
                  {{ scope.opt.label }}
                </q-item-label>
                <q-item-label caption>
                  {{ scope.opt.description }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </q-select>
        <q-checkbox
          v-if="!initialEmail && !currentSite.marketingPermissionsAutoOptIn"
          v-model="user.receiveMarketingEmails"
          class="col-12 q-mb-md"
          :disable="!user.email"
        >
          {{ $t('assertion.happyToReceiveMarketingFrom', { siteName: currentSite.name }) }}.
        </q-checkbox>
        <user-preferred-language-select
          v-if="shouldDisplayPreferredLanguage"
          :user="user"
          class="col-6"
          :read-only="readOnly"
        />
      </div>
      <div
        v-if="shouldDisplayMembershipType"
        class="row q-col-gutter-md q-mt-xs"
      >
        <div class="col-12 text-h6">
          Select Membership Type
        </div>
        <div class="col-12">
          <q-chip
            v-for="membershipTypeData in membershipTypesForUser"
            :key="membershipTypeData.membershipType.id"
            :clickable="membershipTypeData.enabled"
            square
            :style="{ opacity: membershipTypeData.enabled ? '1' : '0.5' } "
            :color="(user.membershipType && user.membershipType.id === membershipTypeData.membershipType.id) ? 'primary' : ''"
            :text-color="(user.membershipType && user.membershipType.id === membershipTypeData.membershipType.id) ? 'white' : ''"
            :label="membershipTypeData.membershipType.name"
            :disable="readOnly"
            data-test-id="member-form-membership-type-option"
            @click="user.membershipType && user.membershipType.id === membershipTypeData.membershipType.id ? showTypeSelector() : (user.membershipType = membershipTypeData.membershipType)"
          />
          <q-icon
            name="help_outline"
            size="sm"
            class="q-ml-sm cursor-pointer"
            @click="showTypeSelector()"
          />
        </div>
        <div class="text-negative">
          {{ membershipTypeError }}
        </div>
        <membership-type-selector ref="membershipTypeSelector" />
      </div>
    </div>
  </model-form>
</template>

<script>
import { date } from 'quasar'
import { geoapifyEnabled, isConsumer, isKiosk } from 'src/config'
import { IsoDate } from 'src/models/model'
import { USER_TYPES } from 'src/models/user/User'
import IsoDateInput from 'src/modules/admin/components/IsoDateInput'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import UserPreferredLanguageSelect from 'src/modules/admin/components/UserPreferredLanguageSelect'
import AddressInput from 'src/modules/auth/components/AddressInput'
import PhoneNumberInput from 'src/modules/consumer/components/phone/PhoneNumberInput'
import MembershipTypeSelector from 'src/modules/register/membership/MembershipTypeSelector'

export default {
  name: 'MemberForm',
  components: {
    AddressInput,
    IsoDateInput,
    MembershipTypeSelector,
    ModelForm,
    PhoneNumberInput,
    UserPreferredLanguageSelect
  },
  context: ['currentUser'],
  configuration: ['currentCircuit', 'currentSite', 'userClasses', 'membershipTypes'],
  models: {
    user: {
      membership: {
        members: true
      },
      membershipType: 'default',
      userClass: 'default',
      authorizedSites: {
        fields: ['id', 'name']
      }
    },
    findUserByEmail: {
      fields: 'search',
      variables () {
        return {
          email: this.user.email || ''
        }
      }
    },
    findUserByName: {
      fields: 'search',
      variables () {
        return {
          firstName: this.user.firstName,
          lastName: this.user.lastName
        }
      },
      skip: isConsumer || isKiosk // This doesn't have access to Vue
    }
  },
  props: {
    submodel: {
      type: Boolean,
      required: false,
      default: true
    },
    simplify: {
      type: Boolean,
      required: false,
      default: false
    },
    employeeCreation: {
      type: Boolean,
      required: false,
      default: false
    },
    showPreferredLanguage: {
      type: Boolean,
      required: false,
      default: false
    },
    isDialog: {
      type: Boolean,
      required: false,
      default: true
    },
    width: {
      type: String,
      required: false,
      default: null
    },
    hideTopRightButton: {
      type: Boolean,
      required: false,
      default: false
    },
    showFooterActions: {
      type: Boolean,
      required: false,
      default: false
    },
    hideHeader: {
      type: Boolean,
      required: false,
      default: false
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    borderless: {
      type: Boolean,
      required: false,
      default: false
    },
    noClosedHooksSetup: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['apply-changes', 'add-model', 'model-created'],
  data () {
    return {
      membershipTypeError: null,
      initialEmail: '',
      isInvoiceUser: false
    }
  },
  computed: {
    shouldDisplayUserClass () {
      const hasType = this.user && !!this.user.userClass

      if (hasType && (this.isInvoiceUser || this.user?.isInvoiceable)) return false

      if (this.simplify) return false

      return true
    },
    shouldDisplayMembershipType () {
      const hasType = this.user && !!this.user.membershipType

      if (hasType && (this.isInvoiceUser || this.user?.isInvoiceable)) return false

      if (this.simplify) return false

      return true
    },
    shouldDisplayPreferredLanguage () {
      return this.showPreferredLanguage && this.currentCircuit.supportsTranslations && this.user.id === this.currentUser.id
    },
    userClassesForSelect () {
      const userClasses = (this.$consumer || this.$kiosk) ? _.filter(this.userClasses.data, 'availableOnline') : this.userClasses.data
      return _.map(userClasses, userClass => {
        return {
          value: userClass,
          label: userClass.name,
          description: userClass.description || userClass.ageDescription,
          disable: false
        }
      })
    },
    membershipTypesForUser () {
      return _(this.membershipTypes.data).filter(membershipType => {
        if (this.employeeCreation && !membershipType.free) return false

        // Membership Modifications on Admin are unrestricted - exit filter early
        if (this.$admin) return true

        if (membershipType.hide || (!membershipType.availableOnline && !this.$register)) return false

        if (!membershipType.excludeSites || membershipType.siteIds?.length > 0) {
          if (membershipType.excludeSites === _.includes(membershipType.siteIds, this.currentSite.id)) {
            return false
          }
        }

        return true
      }).map(membershipType => {
        return {
          membershipType,
          enabled: membershipType.free || !!membershipType.membershipUserTypeForUser(this.user)
        }
      }).compact().value()
    }
  },
  watch: {
    'user.dateOfBirth': {
      handler () {
        this.validateUserClasses()
      },
      immediate: true
    },
    'user.userClass.id': {
      handler () {
        !this.simplify && this.validateMembershipTypes()
      },
      immediate: true
    },
    'user.isInvoiceable' () {
      // On fetch isInvoiceable can change, so ensure it is always set to isInvoiceUser
      this.user.isInvoiceable = this.user.isInvoiceable || this.isInvoiceUser
    },
    'user.id': {
      handler () {
        this.initialEmail = this.user.email
        this.user.isInvoiceable = this.user.isInvoiceable || this.isInvoiceUser

        if (this.user.isInvoiceable) {
          this.user.userClass = _.find(this.userClasses.data, { isDefault: true })
          this.user.membershipType = _.find(this.membershipTypes.data, { default: true })
        }

        if (!this.user.email && (this.currentSite.marketingPermissionsAutoOptIn || this.currentSite.marketingPermissionsAutoSelect)) {
          this.user.receiveMarketingEmails = true
        }
      },
      immediate: true
    }
  },
  created () {
    _.extend(this, { IsoDate, USER_TYPES, geoapifyEnabled })
  },
  methods: {
    show (model, initialData = {}, isInvoiceUser = false) {
      this.isInvoiceUser = isInvoiceUser
      // Hacky way to remove circulare reference
      if (model?.parent?.options?.parent) model.parent.options.parent = null

      this.$refs.modelForm.show(model, initialData)
    },
    handlePhoneChange (phoneObject) {
      if (phoneObject?.isValid?.() && !this.readOnly) {
        this.user.phone = phoneObject.nationalNumber
        this.user.phoneCountryCode = phoneObject.country
      } else if (!this.readOnly) {
        this.user.phone = null
        this.user.phoneCountryCode = null
      }
    },
    validateUserClasses (user = this.user) {
      if (!user.dateOfBirth || user.dateOfBirth.substr(0, 4) === '0000') {
        this.userClassesForSelect.forEach(userClassOption => { userClassOption.disable = false })
        return
      }

      // Make options available based on age
      const birthdate = date.extractDate(user.dateOfBirth, 'YYYY-MM-DD')
      const today = new Date()

      const age = Math.floor(date.getDateDiff(today, birthdate, 'days') / 365)

      if (age < 0 || age > 200) return

      let firstMatch = null
      this.userClassesForSelect.forEach(userClassOption => {
        const min = userClassOption.value.minAge || 0
        const max = userClassOption.value.maxAge || 200

        const match = (age >= min && age <= max)

        userClassOption.disable = !match

        if (!firstMatch && match) firstMatch = userClassOption.value
      })

      if (firstMatch) user.userClass = firstMatch
    },
    validateMembershipTypes (user = this.user) {
      if (!_.some(this.membershipTypesForUser, membershipTypeOption => membershipTypeOption.membershipType.id === user.membershipType?.id && membershipTypeOption.enabled)) {
        // If we are editing, assume they want to change to a paid membership
        const matchingMembershipData = _.find(this.membershipTypesForUser, membershipData => membershipData.enabled && !membershipData.membershipType.free) || _.find(this.membershipTypesForUser, membershipData => membershipData.enabled)
        if (matchingMembershipData) {
          user.membershipType = matchingMembershipData.membershipType
        }
      }
    },
    showTypeSelector () {
      const initialMemberPrice = !this.user.membership || (this.user.membership && this.user.id === this.user.membership.initialMember?.id)
      this.$refs.membershipTypeSelector.show(this.user, initialMemberPrice, false)
    },
    async emailAvailable () {
      // We don't want to do this check if there is no email
      if (!this.user.email || this.user.email === '') return true

      let isEmailTaken = false
      if (this.user.membership?.members?.data) {
        isEmailTaken = !!_.find(this.user.membership.members.data, member => member.id !== this.user.id && member.email === this.user.email)
      }

      await this.findUserByEmail.fetch()
      isEmailTaken = isEmailTaken || (!this.findUserByEmail.isLocal && this.findUserByEmail.id !== this.user.id)
      return !isEmailTaken || 'Email is taken'
    }
  }
}

</script>
